<template lang="pug">
    v-dialog(v-model='dialog' persistent='' max-width='600px')
        template(v-slot:activator="{ on, attrs }")
            v-select(v-model="searchText" @click="init()" v-bind="attrs" v-on="on" label='Customer' :items='items' :menu-props="{maxHeight: 0}" :dense="dense" :outlined="outlined")
        v-card
            v-card-title
                span.text-h5 Search Customers
            v-card-text
                v-container
                    v-row
                        v-col(cols='12')
                            v-autocomplete(v-model='searchOrganizationId' :items='organizationUsers' label='Organization *' item-text="organization_name" item-value="organization_id" clearable)
                    v-row
                        v-col(cols='6')
                            v-text-field(label='First Name' :disabled='!(searchOrganizationId > 0)' type='text' v-model="searchFirstName" clearable)
                        v-col(cols='6')
                            v-text-field(label='Last Name' :disabled='!(searchOrganizationId > 0)' type='text' v-model="searchLastName" clearable)
                    v-row
                        v-col(cols='6')
                            v-text-field(
                                label='Email' 
                                v-model="searchEmail" 
                                type='text' 
                                :disabled='!(searchOrganizationId > 0)' 
                                :error-messages="errorMessageEmail"
                                clearable
                            )
                        v-col(cols='6')
                            v-text-field(
                                label='Phone' 
                                v-model="searchPhone" 
                                type='text' 
                                :disabled='!(searchOrganizationId > 0)' 
                                :error-messages="errorMessagePhone"
                                v-on:keypress="phoneNumberChange"
                                :hint="evaluatedNumberType"
                                clearable
                            )

            v-card-actions
                v-spacer
                    v-btn.ma-2(color="var(--volie-blue)" outlined ripple @click='dialog = false;')
                        v-icon mdi-window-close
                        | Close
                    v-btn.ma-2(color="var(--volie-blue)" outlined ripple @click='dialog = false; reinitSearch();')
                        v-icon mdi-refresh
                        | Reset
                    v-btn.ma-2(color="var(--volie-blue)" outlined ripple @click='dialog = false; search();' :disabled='!eligibleToSearch')
                        v-icon mdi-magnify
                        | Search
</template>

<script lang="ts">
    import Vue from 'vue';

    // Helpers
    import { cleanupEmailAddress } from "@/helpers/email";
    import { cleanupPhoneNumber } from "@/helpers/phone_number";

    export default Vue.extend({
        data() {
            return {
                dialog: false,
                errorMessagePhone: null,
                errorMessageEmail: null,
                evaluatedNumberType: null,
                items: [],
                searchEmail: null,
                searchFirstName: null,
                searchLastName: null,
                searchOrganizationId: null,
                searchPhone: null,
                searchText: null,
                validEmail: false,
                validPhoneNumber: false,
            }
        },
        methods: {
            init() {
                this.searchEmail          = this.email;
                this.searchFirstName      = this.firstName;
                this.searchLastName       = this.lastName;
                this.searchOrganizationId = this.organizationId;
                this.searchPhone          = this.phone;

                this.setSearchText();
            },
            phoneNumberChange(e) {
                
                // Ensure only numbers are entered
                if (isNaN(e.key)) {
                    e.preventDefault();
                }
            },
            reinitSearch() {
                this.resetSearch();
                this.search();
            },
            resetSearch() {
                this.errorMessagePhone = null;
                this.evaluatedNumberType = null;

                this.searchEmail          = null;
                this.searchFirstName      = null;
                this.searchLastName       = null;
                this.searchOrganizationId = null;
                this.searchPhone          = null;
            },
            search() {
                const searchObj = {
                    email:          cleanupEmailAddress(this.searchEmail),
                    firstName:      this.searchFirstName,
                    lastName:       this.searchLastName,
                    organizationId: this.searchOrganizationId,
                    phone:          this.searchPhone,
                };

                this.handleSearch(searchObj);
            },
            setSearchText() {
                // only update search text when it's searched, not every time text is changed
                
                let text = '';

                if (this.firstName?.length > 0) {
                    text = text + this.firstName;
                }

                if (this.lastName?.length > 0) {
                    text = text + ' ' + this.lastName;
                }

                if (this.email?.length > 0) {
                    text = text + ' <' + this.email + '>';
                }

                if (this.phone?.length > 0) {
                    text = text + ' (PN: ' + this.phone + ')';
                }

                if (text?.length > 0) {
                    text = text.trim();

                    // need to set search text as item in items to show current search filter in select text
                    this.items = [text];
                }

                this.searchText = text;

                return;
            },
            validateEmail() {

                // Reset if necessary
                if (this.searchEmail?.length > 0) {
                    const cleaned = cleanupEmailAddress(this.searchEmail);
                    if (cleaned?.length > 0) {
                        this.errorMessageEmail = null;
                        this.validEmail = true;
                    } else {
                        this.errorMessageEmail = "Please enter a valid email address";
                        this.validEmail = false;
                    }
                } else {
                    this.errorMessageEmail = null;
                }

                return;
            },
            validatePhoneNumber() {
                if (this.searchPhone?.length > 0) {
                    const cleanPN = cleanupPhoneNumber(this.searchPhone, null);
                    this.errorMessagePhone = cleanPN.valid === true ? null : "Please enter a valid phone number";
                    this.evaluatedNumberType = cleanPN.valid === true ? cleanPN.numberType[0].toUpperCase() + cleanPN.numberType.slice(1) : null;
                    this.validPhoneNumber = cleanPN.valid === true
                } else {
                    this.errorMessagePhone = null;
                    this.evaluatedNumberType = null;
                }

                return;
            },
        },
        computed: {
            eligibleToSearch() {
                if (!(this.searchOrganizationId > 0)) {
                    return false;
                }
                if (this.searchPhone?.length > 0 && !this.validPhoneNumber) {
                    return false;
                }
          
                if (this.searchEmail?.length > 0 && !this.validEmail) {
                    return false;
                }

                return true;
            },
        },
        props: {
            dense: Boolean,
            email: String,
            handleSearch: Function,
            firstName: String,
            lastName: String,
            organizationId: Number,
            organizationUsers: Array,
            outlined: Boolean,
            phone: String,
        },
        created() {
            this.init();
        },
        watch: {
            "email"() {
                this.setSearchText();
            },
            "firstName"() {
                this.setSearchText();
            },
            "lastName"() {
                this.setSearchText();
            },
            "organizationId"() {
                this.setSearchText();
            },
            "phone"() {
                this.setSearchText();
            },
            "searchPhone"() {
                this.validatePhoneNumber();
            },
            "searchEmail"() {
                this.validateEmail();
            },
        }
    });
</script>

<style lang="scss" scoped>
</style>
